import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Inject,
  ViewChild,
  ElementRef,
  AfterViewInit,
  Renderer2,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { animate, state, style, transition, trigger } from '@angular/animations';

import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter } from 'rxjs/operators';

import { unsubscribe, WindowRef } from '@fcom/core';
import { ButtonMode, ButtonSize, ButtonTheme, IconPosition } from '@fcom/ui-components';
import { ElementActions, ElementTypes, TestVariant, GaContext, ScrollService } from '@fcom/common';
import { GtmService } from '@fcom/common/gtm';
import { isPresent } from '@fcom/core/utils';
import {
  DestinationSearchFeatureState,
  DestinationSearchItem,
  DestinationSearchItemFilterSelection,
  FilterAction,
  DestinationSearchFilters,
  DestinationSearchFilterStateMeta,
} from '@fcom/destination-search/interfaces';
import { TripType } from '@fcom/core/interfaces';

import { setSelectedDestinations } from '../../store/actions/destination-search-filter.actions';

export const STICKY_NAV_HEIGHT = 122;
const FINNAIR_EASE_IN = 'cubic-bezier(0.10, 0.25, 0.55, 1.00)';
const FINNAIR_EASE_OUT = 'cubic-bezier(0.55, 0.25, 0.55, 1.00)';

enum ScrollState {
  SCROLLING_DOWN_BELOW_SEARCH_BAR,
  SCROLLING_UP_BELOW_SEARCH_BAR,
  SCROLLED_TO_SEARCH_BAR_TOP,
  SCROLLED_PAST_SEARCH_BAR_TOP,
}

export enum ANIMATION_STATE {
  DEFAULT = 'default',
  HIDDEN = 'hidden',
  VISIBLE = 'visible',
}

const TRANSFORM_VALUES = {
  [ANIMATION_STATE.DEFAULT]: 'translateY(0)',
  [ANIMATION_STATE.VISIBLE]: 'translateY(0)',
  [ANIMATION_STATE.HIDDEN]: 'translateY(-100%)',
} as const;

@Component({
  selector: 'fin-destination-search-form',
  templateUrl: 'destination-search-form.component.html',
  styleUrls: ['destination-search-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('scrollAnimation', [
      state(
        ANIMATION_STATE.HIDDEN,
        style({
          opacity: 0,
          transform: TRANSFORM_VALUES[ANIMATION_STATE.HIDDEN],
        })
      ),
      state(
        ANIMATION_STATE.VISIBLE,
        style({
          opacity: 1,
          transform: TRANSFORM_VALUES[ANIMATION_STATE.VISIBLE],
        })
      ),
      state(
        ANIMATION_STATE.DEFAULT,
        style({
          opacity: 1,
          transform: TRANSFORM_VALUES[ANIMATION_STATE.DEFAULT],
        })
      ),
      transition(`${ANIMATION_STATE.HIDDEN} => ${ANIMATION_STATE.VISIBLE}`, [animate(`0.5s ${FINNAIR_EASE_IN}`)]),
      transition(`${ANIMATION_STATE.VISIBLE} => ${ANIMATION_STATE.HIDDEN}`, [animate(`0.5s ${FINNAIR_EASE_OUT}`)]),
      transition(`${ANIMATION_STATE.DEFAULT} => ${ANIMATION_STATE.HIDDEN}`, [animate('0s')]),
    ]),
  ],
  standalone: false,
})
export class DestinationSearchFormComponent implements OnInit, OnDestroy, AfterViewInit {
  readonly ANIMATION_STATE = ANIMATION_STATE;

  @Input()
  meta: DestinationSearchFilterStateMeta;

  @Input()
  initialDestinationSearchSelection$: Observable<DestinationSearchFilters>;

  @Input()
  allDestinations$: Observable<DestinationSearchItem[]>;

  @Input()
  showFilters = true;

  @Input()
  clearFilters$: Observable<void>;

  readonly IconPosition = IconPosition;
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonMode = ButtonMode;
  readonly ButtonSize = ButtonSize;
  readonly TripType = TripType;
  readonly TestVariant = TestVariant;

  selectedTripType$: BehaviorSubject<TripType> = new BehaviorSubject(undefined);
  selectedDestinations$: BehaviorSubject<DestinationSearchItem[]> = new BehaviorSubject(undefined);
  subscription: Subscription = new Subscription();
  initialSelection$: Observable<DestinationSearchFilters>;
  currentScrollPosition: number;
  originSearchBarPosition: { top: number; bottom: number };
  stickyEnabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isClient: boolean = false;

  backdropElement: HTMLElement | null = null;
  searchBarOptionListVisible$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  closeSearchBarOptionsList$: BehaviorSubject<void> = new BehaviorSubject(undefined);
  isStickyNavTest$: Observable<boolean> = of(false);
  state$: BehaviorSubject<ANIMATION_STATE> = new BehaviorSubject(ANIMATION_STATE.DEFAULT);
  @ViewChild('container')
  container: ElementRef<HTMLElement>;

  constructor(
    private gtmService: GtmService,
    private store$: Store<DestinationSearchFeatureState>,
    @Inject(PLATFORM_ID) private platform: object,
    private readonly renderer: Renderer2,
    private readonly windowRef: WindowRef,
    private scrollService: ScrollService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnInit(): void {
    this.isClient = isPlatformBrowser(this.platform);
    if (!this.isClient) {
      return;
    }

    this.initialSelection$ = this.initialDestinationSearchSelection$;

    // Take initial tripType value
    this.subscription.add(
      this.initialSelection$.pipe(filter((state) => isPresent(state?.tripType))).subscribe((state) => {
        this.selectedTripType$.next(state.tripType);
        this.selectedDestinations$.next(state.selectedDestinations);
      })
    );

    this.currentScrollPosition = this.windowRef.nativeWindow.scrollY;
  }

  ngAfterViewInit(): void {
    if (this.showFilters) {
      this.originSearchBarPosition = {
        top: this.container.nativeElement.offsetTop - STICKY_NAV_HEIGHT,
        bottom: this.container.nativeElement.offsetTop - STICKY_NAV_HEIGHT + this.container.nativeElement.offsetHeight,
      };

      this.subscription.add(
        this.searchBarOptionListVisible$.subscribe((isVisible) => {
          if (isVisible) {
            this.addBackdrop();
          } else {
            this.removeBackdrop();
          }
        })
      );

      this.subscription.add(
        this.scrollService
          .listen(this.document.body)
          .pipe(filter(Boolean), distinctUntilChanged())
          .subscribe((event) => {
            const scrollTop = Math.abs(event.top);
            const scrollState = this.getScrollState(scrollTop);
            this.currentScrollPosition = scrollTop;
            this.updateScrollStateAndSticky(scrollState);
          })
      );
    }
  }

  ngOnDestroy(): void {
    this.subscription = unsubscribe(this.subscription);
    this.removeBackdrop();
  }

  /**
   * Called when the destinations are added or removed to/from filters
   */
  onFilterToValueChange({ action, destination, selectedDestinations }: DestinationSearchItemFilterSelection): void {
    const newSelectedDestinations = this.getSelectedDestinationsOnFilterChange({
      action,
      destination,
      selectedDestinations,
    });
    this.selectedDestinations$.next(newSelectedDestinations);
    this.store$.dispatch(setSelectedDestinations({ selectedDestinations: newSelectedDestinations, meta: this.meta }));

    const selectedDestinationsTrackingString = this.selectedDestinationsToTrackingString(newSelectedDestinations);
    this.gtmService.trackElement(
      selectedDestinationsTrackingString,
      GaContext.DESTINATION_MAIN_PAGE,
      ElementTypes.SEARCH,
      selectedDestinationsTrackingString,
      ElementActions.SEARCH
    );

    this.windowRef.nativeWindow.scrollTo({ top: this.originSearchBarPosition.top, behavior: 'smooth' });

    this.gtmService.trackElement(
      `${action}-filter`,
      GaContext.DESTINATION_MAIN_PAGE,
      ElementTypes.BUTTON,
      action === FilterAction.REMOVE_ALL || action === FilterAction.CUSTOM
        ? action
        : `${action}-${destination.titleMaster.replace(/ /g, '_')}`,
      ElementActions.CLICK
    );
  }

  onInputFilter($event: boolean): void {
    if ($event && this.currentScrollPosition < this.originSearchBarPosition?.bottom) {
      this.windowRef?.nativeWindow.scrollTo({ top: this.originSearchBarPosition?.top, behavior: 'smooth' });
    }
  }

  private getScrollState(scrollTop: number): ScrollState {
    const scrollingDown = scrollTop > this.currentScrollPosition;
    const belowSearchBarBottom = scrollTop > this.originSearchBarPosition.bottom;
    const currentScrollBelowSearchBarTop = this.currentScrollPosition > this.originSearchBarPosition.top;

    if (scrollingDown && belowSearchBarBottom) {
      return ScrollState.SCROLLING_DOWN_BELOW_SEARCH_BAR;
    }
    if (scrollingDown && scrollTop > this.originSearchBarPosition.top) {
      return ScrollState.SCROLLED_PAST_SEARCH_BAR_TOP;
    }
    if (!scrollingDown && scrollTop > this.originSearchBarPosition.top) {
      return ScrollState.SCROLLING_UP_BELOW_SEARCH_BAR;
    }
    if (!scrollingDown && scrollTop <= this.originSearchBarPosition.top && currentScrollBelowSearchBarTop) {
      return ScrollState.SCROLLED_TO_SEARCH_BAR_TOP;
    }
  }

  private updateScrollStateAndSticky(scrollState: ScrollState): void {
    switch (scrollState) {
      case ScrollState.SCROLLING_DOWN_BELOW_SEARCH_BAR:
        this.state$.next(ANIMATION_STATE.HIDDEN);
        this.closeSearchBarOptionsList$.next();
        break;
      case ScrollState.SCROLLED_PAST_SEARCH_BAR_TOP:
        this.closeSearchBarOptionsList$.next();
        this.stickyEnabled$.next(false);
        this.state$.next(ANIMATION_STATE.DEFAULT);
        break;
      case ScrollState.SCROLLING_UP_BELOW_SEARCH_BAR:
        if (this.stickyEnabled$.getValue() === false) {
          this.stickyEnabled$.next(true);
        }
        this.state$.next(ANIMATION_STATE.VISIBLE);
        break;
      case ScrollState.SCROLLED_TO_SEARCH_BAR_TOP:
        this.stickyEnabled$.next(false);
        this.state$.next(ANIMATION_STATE.DEFAULT);
        break;
    }
  }

  private addBackdrop(): void {
    if (this.backdropElement) {
      return;
    }

    const styles = {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      background: '#12121233',
      'z-index': '4',
    };

    this.backdropElement = this.renderer.createElement('div');
    Object.entries(styles).forEach(([key, value]) => {
      this.renderer.setStyle(this.backdropElement, key, value);
    });

    this.renderer.appendChild(this.document.body, this.backdropElement);
  }

  private removeBackdrop(): void {
    if (this.backdropElement) {
      this.renderer.removeChild(this.document.body, this.backdropElement);
      this.backdropElement = null;
    }
  }

  private getSelectedDestinationsOnFilterChange({
    action,
    destination,
    selectedDestinations,
  }: DestinationSearchItemFilterSelection): DestinationSearchItem[] {
    if (action === FilterAction.ADD) {
      const alreadySelected = selectedDestinations.find((selected) => selected.id === destination.id);
      return alreadySelected ? selectedDestinations : [...selectedDestinations, destination];
    }
    if (action === FilterAction.REMOVE) {
      return selectedDestinations?.filter((selectedDestination) => selectedDestination.id !== destination.id);
    }
    if (action === FilterAction.REMOVE_ALL) {
      return [];
    }
    if (action === FilterAction.CUSTOM) {
      return selectedDestinations;
    }
  }

  private selectedDestinationsToTrackingString(selectedDestinations: DestinationSearchItem[]): string {
    return selectedDestinations?.length > 0
      ? selectedDestinations
          .map((destination: DestinationSearchItem) => {
            return `type:${destination.type}-title:${destination.titleMaster}`;
          })
          .join(',')
      : 'no-filters';
  }

  onOptionsListVisibilityChanged(visible: boolean): void {
    this.searchBarOptionListVisible$.next(visible);
  }

  // There is a bug where position: fixed wont work when the component that has the property
  // is inside an element with a transform property. In this case the modal has the fixed position
  onMobileFiltersOpened(isOpen: boolean): void {
    this.renderer.setStyle(
      this.container.nativeElement,
      'transform',
      isOpen ? 'unset' : TRANSFORM_VALUES[this.state$.getValue()]
    );
    this.renderer.setStyle(this.container.nativeElement, 'z-index', isOpen ? '10' : '1');
  }
}
